import type { CSSProperties } from "react";
import type { WalterSnapshot } from "../core/types";

interface AuxDisplayProps {
  snapshot: WalterSnapshot;
  compact?: boolean;
}

const toolGlyphs = {
  memory: "M",
  vision: "◉",
  network: "⌁",
  message: "↗",
  system: "S",
  timer: "T",
  news: "N",
};

export function ThirdEyeDisplay({ snapshot, compact = false }: AuxDisplayProps) {
  const active = ["aware", "listening", "thinking", "tool", "speaking"].includes(snapshot.state) || snapshot.environment === "vision";
  const intent = snapshot.state === "sleeping"
    ? "sleep"
    : snapshot.tool.phase !== "idle" && snapshot.tool.phase !== "complete"
      ? "transfer"
      : snapshot.state === "listening"
        ? "listen"
        : snapshot.state === "thinking"
          ? "resolve"
          : snapshot.state === "speaking"
            ? "commune"
            : snapshot.state === "aware"
              ? "awaken"
              : snapshot.attention.present ? "track" : "idle";
  const transferGlyph = snapshot.transfer?.kind === "speech" ? "≈" : snapshot.transfer?.kind === "wake" ? "✦" : toolGlyphs[snapshot.transfer?.kind ?? snapshot.tool.type];
  return (
    <section
      className={`aux-display third-eye intent-${intent} ${active ? "is-active" : ""} tool-${snapshot.tool.phase} transfer-${snapshot.transfer?.phase ?? "none"} direction-${snapshot.transfer?.direction ?? "none"} device-${compact ? "online" : snapshot.devices["third-eye"]} quality-${snapshot.settings.performanceMode} ${compact ? "is-compact" : ""}`}
      data-testid="third-eye-display"
      aria-label={`Walter third eye, ${snapshot.state}`}
      style={{ "--audio": snapshot.audioLevel, "--brightness": snapshot.settings.brightness, "--attention": snapshot.attention.confidence, "--tool-progress": snapshot.tool.progress ?? 0, "--gaze-x": snapshot.gaze.x, "--gaze-y": snapshot.gaze.y, "--transfer-energy": snapshot.transfer?.energy ?? 0, "--transfer-duration": `${snapshot.transfer?.durationMs ?? 1700}ms` } as CSSProperties}
    >
      <div className="aux-void" />
      <div className="organ-texture third-eye-core-texture organ-atlas" aria-hidden="true" />
      <svg className="third-eye-geometry" viewBox="0 0 240 240" aria-hidden="true">
        <defs>
          <radialGradient id="eyeCore" cx="50%" cy="42%">
            <stop offset="0" stopColor="#d9ffff" stopOpacity=".98" />
            <stop offset=".16" stopColor="#62f4ff" stopOpacity=".9" />
            <stop offset=".46" stopColor="#08758b" stopOpacity=".54" />
            <stop offset="1" stopColor="#00151c" stopOpacity="0" />
          </radialGradient>
          <filter id="eyeGlow"><feGaussianBlur stdDeviation="3.2" /></filter>
        </defs>
        <circle className="aux-tick-ring" cx="120" cy="120" r="99" />
        <circle className="aux-orbit orbit-a" cx="120" cy="120" r="77" />
        <circle className="aux-orbit orbit-b" cx="120" cy="120" r="56" />
        <path className="cognition-thread" d="M26 151C61 121 76 70 120 49C164 70 179 121 214 151" />
        <path className="cognition-thread cognition-thread-b" d="M31 88C72 105 89 182 120 197C151 182 168 105 209 88" />
        <g className="dream-lattice">
          <path d="M63 125C83 87 113 75 151 88C169 94 181 109 184 126" />
          <path d="M70 139C94 157 132 160 164 137" />
          <circle cx="84" cy="100" r="3" /><circle cx="155" cy="104" r="2" /><circle cx="121" cy="149" r="2.5" />
        </g>
        <circle className="eye-aura" cx="120" cy="120" r="43" fill="url(#eyeCore)" filter="url(#eyeGlow)" />
      </svg>
      <div key={snapshot.transfer?.id ?? snapshot.tool.nonce} className="aux-tool-glyph" aria-hidden="true">
        {transferGlyph}
      </div>
      <div key={`eye-transfer-${snapshot.transfer?.id ?? snapshot.tool.nonce}`} className={`organ-transfer eye-transfer phase-${snapshot.tool.phase} transfer-${snapshot.transfer?.phase ?? "none"}`} aria-hidden="true">
        <i className="phase-filament" /><b className="phase-fold" /><span>{transferGlyph}</span>
      </div>
      <div className="dream-motes" aria-hidden="true"><i /><i /><i /></div>
      <div className="cognition-stage"><i /><span>{intent === "transfer" ? snapshot.tool.type : intent}</span></div>
      <div className="eye-signal-scar" aria-hidden="true" />
    </section>
  );
}

export function HeartDisplay({ snapshot, compact = false }: AuxDisplayProps) {
  const rate = snapshot.state === "sleeping" ? 7 : snapshot.state === "speaking" ? 1.05 - snapshot.audioLevel * 0.45 : snapshot.state === "aware" ? 1.35 : 2.8;
  return (
    <section
      className={`aux-display heart-display state-${snapshot.state} emotion-${snapshot.emotion} transfer-${snapshot.transfer?.phase ?? "none"} direction-${snapshot.transfer?.direction ?? "none"} device-${compact ? "online" : snapshot.devices.heart} quality-${snapshot.settings.performanceMode} ${compact ? "is-compact" : ""}`}
      data-testid="heart-display"
      aria-label={`Walter heart, ${snapshot.emotion}`}
      style={{ "--audio": snapshot.audioLevel, "--brightness": snapshot.settings.brightness, "--voice": snapshot.audio.outputLevel, "--mic": snapshot.audio.micLevel, "--heart-rate": `${rate}s`, "--transfer-energy": snapshot.transfer?.energy ?? 0, "--transfer-duration": `${snapshot.transfer?.durationMs ?? 2250}ms` } as CSSProperties}
    >
      <div className="aux-void" />
      <div className="organ-texture heart-core-texture organ-atlas" aria-hidden="true" />
      <svg className="heart-geometry" viewBox="0 0 240 240" aria-hidden="true">
        <defs>
          <radialGradient id="heartCore">
            <stop offset="0" stopColor="#ecffff" stopOpacity="1" />
            <stop offset=".18" stopColor="#71f5ff" stopOpacity=".95" />
            <stop offset=".52" stopColor="#078098" stopOpacity=".48" />
            <stop offset="1" stopColor="#00212b" stopOpacity="0" />
          </radialGradient>
          <filter id="heartGlow"><feGaussianBlur stdDeviation="5" /></filter>
        </defs>
        <circle className="aux-tick-ring" cx="120" cy="120" r="99" />
        <path className="heart-orbit orbit-a" d="M31 120C64 82 89 57 120 31C151 57 176 82 209 120C176 158 151 183 120 209C89 183 64 158 31 120Z" />
        <path className="heart-orbit orbit-b" d="M54 120C80 96 96 80 120 54C144 80 160 96 186 120C160 144 144 160 120 186C96 160 80 144 54 120Z" />
        <circle className="heart-aura" cx="120" cy="120" r="55" fill="url(#heartCore)" filter="url(#heartGlow)" />
        <path className="heart-core" d="M120 163C91 143 72 126 72 101C72 81 88 69 103 69C113 69 119 75 120 82C121 75 127 69 137 69C152 69 168 81 168 101C168 126 149 143 120 163Z" />
        <path className="heart-signal" d="M34 121H70L82 101L99 143L117 89L134 132L147 112L158 121H206" />
        <circle className="heart-satellite satellite-a" cx="120" cy="27" r="3" />
        <circle className="heart-satellite satellite-b" cx="213" cy="120" r="3" />
        <circle className="heart-satellite satellite-c" cx="120" cy="213" r="3" />
      </svg>
      <div className="heart-echo"><i /><i /><i /><i /></div>
      <div key={`heart-transfer-${snapshot.transfer?.id ?? snapshot.state}`} className={`organ-transfer heart-transfer transfer-${snapshot.transfer?.phase ?? "none"}`} aria-hidden="true"><i className="pressure-core" /><b /><b /><b /></div>
      <div className="heart-vitality" aria-hidden="true"><i /><span /></div>
    </section>
  );
}
